import { Injectable, inject, signal } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdGateInfoOpenBehavior } from '../bodygraph/gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../bodygraph/chart-tabs/hd-chart-global-settings';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdCenterFearsPanelComponent } from './hd-center-fears-panel.component';
import {
  HdCenterFearsPanelData,
  HdFearCenterKey,
  buildHdCenterFearsPanelKey,
  getHdFearCenter,
  hdFearCenterForGate,
} from './hd-fears.metadata';

/**
 * Opent het per-center angsten-paneel (Bewustzijn & angsten).
 *
 * Volgt hetzelfde patroon als `HdGateGraphPanelService`: de dock-/dialoogkeuze
 * komt uit de globale `gateInfoOpenBehavior`-setting die de chart-pagina's
 * live doorgeven via {@link setOpenBehavior}. Boektekst → altijd de
 * `'full'`-dockzone. Eén paneel per centrum (key `center-fears:<center>`);
 * opnieuw openen voor een andere poort van hetzelfde centrum hergebruikt het
 * paneel en scrollt via `focusGate` naar de juiste sectie.
 */
@Injectable({ providedIn: 'root' })
export class HdFearsPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly openBehavior = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehavior.set(behavior);
  }

  /** Opent het angsten-paneel van een bewustzijnscentrum (zonder focus-poort). */
  openForCenter(center: HdFearCenterKey, origin?: { x: number; y: number }): void {
    this.open(center, null, origin);
  }

  /**
   * Opent het angsten-paneel van het centrum waar deze poort bij hoort en
   * scrollt naar de detailsectie van de poort. Geen fear-poort → no-op.
   */
  openForGate(gate: number, origin?: { x: number; y: number }): void {
    const center = hdFearCenterForGate(gate);
    if (center == null) {
      return;
    }
    this.open(center.key, gate, origin);
  }

  private open(center: HdFearCenterKey, focusGate: number | null, origin?: { x: number; y: number }): void {
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
    const data: HdCenterFearsPanelData = { center, focusGate };

    const ref = this.floatingPanels.openOrFocus<HdCenterFearsPanelComponent, HdCenterFearsPanelData>({
      key: buildHdCenterFearsPanelKey(center),
      component: HdCenterFearsPanelComponent,
      data,
      dockTab: { label: this.dockTabLabel(center), icon: 'face-fearful' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null
        ? { preferred: origin, hint: 'prefer-right-of-anchor' }
        : 'center',
    });
    // Ook een al open paneel de nieuwe focus-poort aanreiken (verse referentie
    // zodat de auto-scroll opnieuw wapent, ook bij dezelfde poort).
    ref.replaceData({ ...data });
  }

  /** Korte center-naam ("Milt", "Ajna", …) uit de awareness-scope, met fallback. */
  private dockTabLabel(center: HdFearCenterKey): string {
    const definition = getHdFearCenter(center);
    const lang = this.contentLang.effectiveLang();
    const title =
      this.gateInfo.cheatSheetTextIn('awareness', `centers.${definition.key}.title`, lang) ||
      this.gateInfo.cheatSheetTextIn('awareness', `centers.${definition.key}.title`, 'nl');
    return title || 'Angsten';
  }
}
